import {
  groupBy,
  identity,
  pipe,
  prop,
  sortBy,
  values,
} from 'ramda'
import { createSelector } from 'redux-bundler'

import {
  doEntitiesReceived,
  doEntitiesRemoved,
  ENTITIES_CLEARED,
  ENTITIES_RECEIVED,
  ENTITIES_REMOVED,
  entityReducerFactory,
  entitySelectorsFactory,
} from '~/src/Lib/createEntityBundle'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_ARRAY, EMPTY_OBJECT, formattedNumber } from '~/src/Lib/Utils'

const logger = createLogger('Store#entities')

export const names = [
  // sensor related
  'models',
  'units',
  // facility related
  'organizations',
  'permissions',
  'zones',
  'licenses',
  'lightSchedules',
  'wasteMethods',
  'wasteReasons',
  'packageAdjustmentReasons',
  // misc
  'noteCategories',
  'packageStatuses',
  'itemCategories',
  'notifications',
  'notificationTypes',
  'users',
  // METRC
  'metrcHarvests',
  // cultivar related
  'kpiRecipes'
]

const entitySelectors = names.reduce(
  (acc, name) => ({ ...acc, ...entitySelectorsFactory({ name }) }),
  {}
)

export const ENTITY_ALTERNATE_LOOKUPS = {
  harvestPhases: 'phases',
}

const valueTemplate = '{{ value }}'
const formatter = (entity, value, precision = entity.precision) => (entity.template || valueTemplate).replace(
  valueTemplate,
  !Number.isNaN(Number(value))
    ? formattedNumber(Number(value) || 0, Number(precision) || 0)
    : value
)

/**
 * Merges dirty and adds inflight to an entity
 * @param {Object} entity - The entity to annotate
 * @param {Object<string, any>} state - The current redux state object
 * @param {string} entityKey - An entity schema name: rooms, harvests, units, etc.
 * @param {number} entityId - The id of the entity to annotate
 * @returns {Object} - The annotated entity: { ...entity, ...dirty[entityId], inflight: inflight[entityId] }
 */
const annotateEntity = (entity, state, entityKey, entityId) => {
  if (entity && entityKey in state && state[entityKey].dirty && state[entityKey].inflight) {
    const { dirty, inflight } = state[entityKey]
    return { ...entity, ...dirty[entityId], inflight: inflight[entityId] }
  }
  return entity
}

const proxifyTemplateHandler = {
  get: (target, key) => {
    if (key === 'format' && target.template && !(key in target)) {
      target.format = formatter.bind(null, target)
    }
    return target[key]
  },
  has: (target, key) => {
    if (key in target) return true
    return key === 'format'
  },
  ownKeys: target => {
    const realKeys = Reflect.ownKeys(target)
    if (realKeys.includes('format')) {
      return realKeys
    }
    return [...realKeys, 'format']
  }
}
export const proxifyTemplateEntity = entity => new Proxy(entity, proxifyTemplateHandler)
const proxifyEntities = (entities = EMPTY_OBJECT, state = EMPTY_OBJECT, entityKey = '') => {
  const cache = {}
  return new Proxy(entities, {
    get: (target, key) => {
      let found = target[key]
      if (!found) return found
      if (key in cache) return cache[key]
      found = annotateEntity(found, state, entityKey, key)
      if (typeof found.template === 'string' && found.template.includes(valueTemplate)) {
        found = proxifyTemplateEntity(found)
      }
      cache[key] = found
      return found
    },
    ownKeys: target => (
      target === EMPTY_OBJECT
        ? EMPTY_ARRAY
        : Reflect.ownKeys(target)
    )
  })
}

export default (entityBundles = []) => {
  const proxifyEntitiesContainer = ({ entities: container, ...state }) => {
    const cache = {}
    return new Proxy((container), {
      get: (target, key) => {
        if (typeof key !== 'string') {
          return undefined
        }
        let entityKey = !target[key] && key in ENTITY_ALTERNATE_LOOKUPS
          ? ENTITY_ALTERNATE_LOOKUPS[key]
          : key
        let entityId
        const found = cache[entityKey] ?? target[entityKey]
        if (found === undefined && key.includes('_')) {
          [entityKey, entityId] = key.split('_')
          entityKey = !target[entityKey] && entityKey in ENTITY_ALTERNATE_LOOKUPS
            ? ENTITY_ALTERNATE_LOOKUPS[entityKey]
            : entityKey
          cache[entityKey] ??= proxifyEntities(target[entityKey], state, entityKey)
          if (entityId in cache[entityKey]) {
            return cache[entityKey][entityId]
          }
          return undefined
        }
        if (found == null) return found

        cache[entityKey] ??= proxifyEntities(found, state, entityKey)
        return cache[entityKey]
      },
      ownKeys: target => (
        target === EMPTY_OBJECT
          ? EMPTY_ARRAY
          : [...Reflect.ownKeys(target), ...Reflect.ownKeys(ENTITY_ALTERNATE_LOOKUPS)]
      )
    })
  }

  const entityManager = {
    name: 'entities',
    reducer: entityReducerFactory(),
    doClearEntities: () => ({ type: ENTITIES_CLEARED }),
    doEntitiesReceived,
    doEntitiesRemoved,
    selectCurrents: createSelector(
      identity,
      'selectEntities',
      (root, entities) => Object.keys(entities).reduce((currents, key) => {
        const finalKey = key in ENTITY_ALTERNATE_LOOKUPS ? ENTITY_ALTERNATE_LOOKUPS[key] : key
        return finalKey in root && 'current' in root[finalKey]
          ? { ...currents, [key]: root[finalKey].current }
          : currents
      }, EMPTY_OBJECT)
    ),
    selectEntities: createSelector(
      identity,
      proxifyEntitiesContainer
    ),
    selectNoteCategoriesByName: createSelector(
      'selectNoteCategories',
      categories => Object.values(categories).reduce(
        (acc, category) => ({
          ...acc,
          [category.name.toLowerCase()]: category,
        }),
        EMPTY_OBJECT
      )
    ),
    selectPermissionsByCategory: createSelector(
      'selectPermissions',
      pipe(values, sortBy(prop('sequence')), groupBy(prop('categoryName')))
    ),
    selectPermissionsByKey: createSelector(
      'selectPermissions',
      permissions => Object.values(permissions).reduce((p, c) => {
        p[c.key] = { ...c }
        return p
      }, {})
    ),
    ...entitySelectors,
    // TODO: Scoped selector to fix blocking bug; other selectors may need a similar fix
    ...entitySelectorsFactory({
      name: 'wasteTypes',
      scope: {
        selector: 'selectCurrentFacility',
        test: (currentFacility, wasteType) => (
          Array.isArray(currentFacility.wasteTypes)
            ? (
              currentFacility && wasteType
              && currentFacility.wasteTypes.includes(wasteType.id)
            )
            : false
        )
      },
    }),
    persistActions: [ENTITIES_RECEIVED, ENTITIES_REMOVED],
  }

  return entityBundles.concat(entityManager)
}
